/**
 * Reports API helpers (Phase F α).
 *
 * - Saved-report CRUD against `lbs_saved_reports`.
 * - Canned report runners that re-use existing booking / issue / sampling
 *   tables. All bookings queries are filtered by client_id when a view-as
 *   client is set.
 */
import { supabase } from "@/lib/supabaseClient";

/* ─────────  Saved-report CRUD  ───────── */

export async function listSavedReports(clientId) {
  let q = supabase
    .from("lbs_saved_reports")
    .select("id,name,description,definition,is_shared,client_id,created_by_user_id,created_at")
    .order("created_at", { ascending: false })
    .limit(200);
  if (clientId) q = q.eq("client_id", clientId);
  return q;
}

export async function getSavedReport(id) {
  return supabase.from("lbs_saved_reports").select("*").eq("id", id).maybeSingle();
}

export async function upsertSavedReport(values) {
  const payload = { ...values, updated_at: new Date().toISOString() };
  if (payload.id) {
    const { id, ...rest } = payload;
    return supabase.from("lbs_saved_reports").update(rest).eq("id", id).select().maybeSingle();
  }
  return supabase.from("lbs_saved_reports").insert(payload).select().maybeSingle();
}

export async function deleteSavedReport(id) {
  return supabase.from("lbs_saved_reports").delete().eq("id", id);
}

/* ─────────  Range helpers  ───────── */

export function resolveRange(range, customFrom, customTo) {
  const now = new Date();
  const startOfDay = new Date(now); startOfDay.setHours(0, 0, 0, 0);
  const endOfDay   = new Date(now); endOfDay.setHours(23, 59, 59, 999);

  if (range === "today") {
    return { from: startOfDay.toISOString(), to: endOfDay.toISOString() };
  }
  if (range === "this_week") {
    const sow = new Date(startOfDay); sow.setDate(sow.getDate() - sow.getDay());
    return { from: sow.toISOString(), to: endOfDay.toISOString() };
  }
  if (range === "this_month") {
    const som = new Date(startOfDay); som.setDate(1);
    return { from: som.toISOString(), to: endOfDay.toISOString() };
  }
  if (range === "last_30d") {
    const from = new Date(startOfDay); from.setDate(from.getDate() - 30);
    return { from: from.toISOString(), to: endOfDay.toISOString() };
  }
  if (range === "last_90d") {
    const from = new Date(startOfDay); from.setDate(from.getDate() - 90);
    return { from: from.toISOString(), to: endOfDay.toISOString() };
  }
  if (range === "custom" && customFrom && customTo) {
    return {
      from: new Date(`${customFrom}T00:00:00.000Z`).toISOString(),
      to:   new Date(`${customTo}T23:59:59.999Z`).toISOString(),
    };
  }
  // default = this month
  const som = new Date(startOfDay); som.setDate(1);
  return { from: som.toISOString(), to: endOfDay.toISOString() };
}

/* ─────────  Booking-data fetcher (re-used by all canned reports)  ───────── */

export async function fetchBookingsForReport({ clientId, siteId, from, to, limit = 5000, full = false }) {
  let q = supabase
    .from("lbs_bookings")
    .select(full ? `*,
      site:lbs_sites!lbs_bookings_site_id_fkey(id,name,code,timezone),
      bay:lbs_bays!lbs_bookings_bay_id_fkey(id,name,code,color)` : `
      id, reference_number, status, direction,
      scheduled_start, scheduled_end,
      site_id, bay_id, client_id,
      sender_company, receiver_company,
      vehicle_type, assigned_driver_id, forklift_driver_user_id,
      pallets_in, pallets_out, actual_pallets_in, actual_pallets_out, pallet_type,
      external_ref, internal_ref,
      site:lbs_sites!lbs_bookings_site_id_fkey(id,name,code),
      bay:lbs_bays!lbs_bookings_bay_id_fkey(id,name,code,color)
    `)
    .gte("scheduled_start", from)
    .lte("scheduled_start", to)
    .order("scheduled_start", { ascending: false })
    .limit(limit);
  if (clientId) q = q.eq("client_id", clientId);
  if (siteId)   q = q.eq("site_id",   siteId);
  return q;
}

/* ─────────  Pallet hire/dehire fetcher  ───────── */

/** Returns one row per booking that has any pallet IN/OUT activity in range. */
export async function fetchPalletHireDehire({ clientId, siteId, from, to, brand = null }) {
  let q = supabase
    .from("lbs_bookings")
    .select(`
      id, reference_number, scheduled_start, status, direction,
      sender_company, receiver_company,
      pallet_type, pallets_in, pallets_out, actual_pallets_in, actual_pallets_out,
      external_ref, internal_ref, client_id,
      site:lbs_sites!lbs_bookings_site_id_fkey(id,name,code)
    `)
    .gte("scheduled_start", from)
    .lte("scheduled_start", to)
    .or(
      "pallets_in.gt.0,pallets_out.gt.0,actual_pallets_in.gt.0,actual_pallets_out.gt.0"
    )
    .order("scheduled_start", { ascending: false })
    .limit(5000);
  if (clientId) q = q.eq("client_id", clientId);
  if (siteId)   q = q.eq("site_id",   siteId);
  if (brand)    q = q.eq("pallet_type", brand);
  return q;
}

/* ─────────  CSV helper  ───────── */
export function downloadCsv(filename, rows, columns) {
  // columns: [{ key, label, format?(v, row)=>string }]
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = columns.map((c) => escape(c.label)).join(",");
  const body = rows.map((r) =>
    columns
      .map((c) => {
        const raw = c.format ? c.format(r[c.key], r) : r[c.key];
        return escape(raw);
      })
      .join(",")
  ).join("\n");
  const csv = `${header}\n${body}\n`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}
